<template>
  <div class="page-container">
    <!-- Hero Section -->
    <section class="hero-section">
      <video autoplay loop muted playsinline class="hero-video">
        <source
          :src="
            getCDNlink('assets/new-assets/Products/Product_Page_Banner.webm')
          "
          type="video/webm"
        />
        Your browser does not support the video tag.
      </video>
      <div class="hero-overlay"></div>
      <div class="hero-content">
        <h1 class="hero-title">
          Business Intelligence <span class="blue-text">Products</span>
          <br />
          Built for Decision <span class="blue-text">Makers</span>
        </h1>
        <p class="hero-subtitle">
          Equip your team with powerful tools for due diligence, onboarding,
          <br />
          and global trade analysis - all in one platform.
        </p>
      </div>
    </section>

    <!-- Philosophy Section -->
    <section class="philosophy-section">
      <h2 class="section-title">Our Philosophy</h2>
      <p class="section-subtitle">
        At Credence, we believe intelligence is not static - it's dynamic,
        real-time, and context-driven.
        <br />
        Our philosophy is grounded in the power of:
      </p>

      <div class="philosophy-grid">
        <div class="philosophy-card">
          <div class="icon-container">
            <div class="icon-box">
              <div class="icon" v-html="svgIcons.database"></div>
              <div class="icon-separator"></div>
              <div class="icon" v-html="svgIcons.code"></div>
            </div>
          </div>
          <h3 class="card-title">Smart Data</h3>
          <p class="card-text">
            We process billions of data points from official sources, enriched
            by technology for contextual depth.
          </p>
        </div>

        <div class="philosophy-card">
          <div class="icon-container">
            <div class="icon-box">
              <div class="icon" v-html="svgIcons.network"></div>
              <div class="icon-separator"></div>
              <div class="icon" v-html="svgIcons.rocket"></div>
            </div>
          </div>
          <h3 class="card-title">Technology First</h3>
          <p class="card-text">
            Our technology stack ensures actionable insights at speed.
          </p>
        </div>

        <div class="philosophy-card">
          <div class="icon-container">
            <div class="icon-box">
              <div class="icon" v-html="svgIcons.cube"></div>
              <div class="icon-separator"></div>
              <div class="icon" v-html="svgIcons.shield"></div>
            </div>
          </div>
          <h3 class="card-title">Built for Trust</h3>
          <p class="card-text">
            Transparency, explainability, and compliance drive every product we
            build.
          </p>
        </div>

        <div class="philosophy-card">
          <div class="icon-container">
            <div class="icon-box">
              <div class="icon" v-html="svgIcons.nodes"></div>
              <div class="icon-separator"></div>
              <div class="icon" v-html="svgIcons.globe"></div>
            </div>
          </div>
          <h3 class="card-title">Asia-Centric, Globally Ready</h3>
          <p class="card-text">
            Specialized in Asia's trade corridors, with global applicability.
          </p>
        </div>
      </div>
    </section>

    <!-- Product Suite Section -->
    <section class="product-suite-section">
      <h2 class="section-title">Our Product Suite</h2>
      <p class="section-subtitle">
        Credence provides AI-powered business intelligence solutions that enable
        banks and corporates to
        <br />
        make confident, informed trade finance decisions quickly and accurately.
      </p>

      <div class="product-cards">
        <!-- Business Information Reports -->
        <div class="product-card">
          <div class="product-content">
            <img
              :src="getCDNlink('assets/new-assets/Products/Business_Info.gif')"
              alt="Business Information Reports"
              class="product-gif"
            />
            <h3 class="product-title">Business Information Reports</h3>
            <p class="product-subtitle">Reliable, detailed company insights</p>
            <p class="product-description">
              Our business information reports are generated through a
              technology-first process that combines automated data extraction
              and intelligent validation. We aggregate structured data from
              official sources - public registries, government filings, customs
              records, court databases, and compliance systems - ensuring high
              accuracy and coverage.
            </p>
          </div>
          <img
            :src="getCDNlink('assets/new-assets/Products/Business_Info.webp')"
            alt="Business Information Reports"
            class="product-image"
          />
        </div>

        <!-- Insight Lens -->
        <div class="product-card">
          <div class="product-content">
            <img
              :src="getCDNlink('assets/new-assets/Products/Insight_Lens.gif')"
              alt="Insight Lens"
              class="product-gif"
            />
            <h3 class="product-title">Insight Lens</h3>
            <p class="product-subtitle">
              Interactive, natural-language reports
            </p>
            <p class="product-description">
              Discover how our intelligent reports and conversational insights
              transform raw data into cross-border clarity.
            </p>
          </div>
          <div class="product-video-container">
            <video autoplay loop muted playsinline class="product-video">
              <source
                :src="
                  getCDNlink('assets/new-assets/Products/Insight_Lens.webm')
                "
                type="video/webm"
              />
            </video>
          </div>
        </div>

        <!-- Creda Chatbot -->
        <div class="product-card">
          <div class="product-content">
            <img
              :src="getCDNlink('assets/new-assets/Products/Creda.gif')"
              alt="Creda Chatbot"
              class="product-gif"
            />
            <h3 class="product-title">Creda Chatbot</h3>
            <p class="product-subtitle">
              Instant conversational business insights
            </p>
            <p class="product-description">
              Get answers in seconds across multiple companies with
              conversational intelligence - no more reading lengthy reports,
              just ask and know. Analyze multiple entities in one conversation -
              replacing the old "Download & Read" model with a dynamic "Ask &
              Know" experience.
            </p>
            <div class="development-badge">Limited Release</div>
          </div>
          <img
            :src="getCDNlink('assets/new-assets/Products/Creda.webp')"
            alt="Creda Chatbot"
            class="product-image"
          />
        </div>

        <!-- Global Company Search -->
        <div class="product-card">
          <div class="product-content">
            <img
              :src="getCDNlink('assets/new-assets/Products/Global.gif')"
              alt="Global Company Search"
              class="product-gif"
            />
            <h3 class="product-title">Global Company Search</h3>
            <p class="product-subtitle">Worldwide access to company profiles</p>
            <p class="product-description">
              Access our database of 350 million+ entities worldwide. Find
              detailed information on companies across global markets. Embed
              intelligence into your workflows with our powerful Search API.
            </p>
          </div>
          <img
            :src="getCDNlink('assets/new-assets/Products/Global.webp')"
            alt="Global Company Search"
            class="product-image"
          />
        </div>

        <!-- Trade Risk Intelligence -->
        <div class="product-card">
          <div class="product-content">
            <img
              :src="getCDNlink('assets/new-assets/Products/Trade_Risk.gif')"
              alt="Trade Risk Intelligence"
              class="product-gif"
            />
            <h3 class="product-title">Trade Risk Intelligence</h3>
            <p class="product-subtitle">
              Advanced fraud detection & comprehensive trade analysis
            </p>
            <p class="product-description">
              Ready to dive into the world of international trade? Our reports
              include everything you need, from HSN codes to shipment details
              and frequency. It's your comprehensive guide to making informed
              decisions for supplier Onboarding, Importing or Exporting.
            </p>
          </div>
          <img
            :src="getCDNlink('assets/new-assets/Products/Trade_Risk.webp')"
            alt="Trade Risk Intelligence"
            class="product-image"
          />
        </div>
      </div>
    </section>

    <!-- Data Coverage Section -->
    <section class="data-coverage-section">
      <h2 class="section-title">Our Data Coverage</h2>
      <p class="section-subtitle">
        Our products harness a wide range of verified, real-time data sources:
      </p>

      <div class="data-grid">
        <div class="data-item">
          <div class="data-icon" v-html="svgIcons.riskScore"></div>
          <p class="data-text">Risk Score and Summary</p>
        </div>
        <div class="data-item">
          <div class="data-icon" v-html="svgIcons.registration"></div>
          <p class="data-text">Registration Details</p>
        </div>
        <div class="data-item">
          <div class="data-icon" v-html="svgIcons.financials"></div>
          <p class="data-text">Financials and Analysis</p>
        </div>
        <div class="data-item">
          <div class="data-icon" v-html="svgIcons.shareholding"></div>
          <p class="data-text">Shareholding</p>
        </div>
        <div class="data-item">
          <div class="data-icon" v-html="svgIcons.management"></div>
          <p class="data-text">Management Details</p>
        </div>
        <div class="data-item">
          <div class="data-icon" v-html="svgIcons.peer"></div>
          <p class="data-text">Peer Comparison</p>
        </div>
        <div class="data-item">
          <div class="data-icon" v-html="svgIcons.related"></div>
          <p class="data-text">Related Parties</p>
        </div>
        <div class="data-item">
          <div class="data-icon" v-html="svgIcons.bank"></div>
          <p class="data-text">Bank Relationships</p>
        </div>
        <div class="data-item">
          <div class="data-icon" v-html="svgIcons.legal"></div>
          <p class="data-text">Legal History</p>
        </div>
        <div class="data-item">
          <div class="data-icon" v-html="svgIcons.tax"></div>
          <p class="data-text">Tax Compliance</p>
        </div>
        <div class="data-item">
          <div class="data-icon" v-html="svgIcons.aml"></div>
          <p class="data-text">AML/KYC Checks</p>
        </div>
        <div class="data-item">
          <div class="data-icon" v-html="svgIcons.trade"></div>
          <p class="data-text">International Trade History</p>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <StartJourney />
  </div>
  <Footer class="footer-instance"></Footer>
</template>

<script>
import { svgIcons } from "@/assets/Icons/productsIcons.js";
import StartJourney from "@/components/Product/StartJourney.vue";
import Footer from "@/components/HomePage/Footer/Footer.vue";
import config from "@/config";

export default {
  name: "ProductsPage",
  components: {
    StartJourney,
    Footer,
  },
  data() {
    return {
      svgIcons,
    };
  },
  methods: {
    getCDNlink(fileKey) {
      let baseUrl = "";
      if (
        process.env.NODE_ENV === "development" ||
        process.env.NODE_ENV === "staging"
      ) {
        baseUrl = config.stagingCDN;
      } else if (process.env.NODE_ENV === "production") {
        baseUrl = config.prodCDN;
      }

      return `${baseUrl}${fileKey}`;
    },
  },
};
</script>

<style scoped>
/* Global Styles */
.page-container {
  font-family: "Inter", "Helvetica Neue", Arial, sans-serif;
  color: #333;
  line-height: 1.5;
  overflow-x: hidden;
  min-height: 100vh;
  /* padding: 0 20px; */
}

.section-title {
  align-self: stretch;
  text-align: center;
  font-family: "Figtree";
  font-size: 48px;
  font-style: normal;
  font-weight: 600;
  line-height: 56px;
  letter-spacing: 0.56px;
  background: var(
    --Section-Title-Gradient,
    linear-gradient(288deg, #046BE1 19%, #224096 64%)
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
}

.section-subtitle {
  color: var(--Raisin-Black, #1c1c1c);
  text-align: center;
  font-family: "Figtree";
  font-size: 24px;
  font-weight: 400;
  line-height: 120%;
}

/* Hero Section */
.hero-section {
  position: relative;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  overflow: hidden;
}

.hero-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(
    119.63% 62.25% at 50% 50%,
    #000000eb 0%,
    rgba(0, 0, 0, 0) 100%
  );
  z-index: 2;
}

.hero-content {
  position: relative;
  z-index: 3;
  display: flex;
  padding: 0px 160px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;
}

.hero-title {
  text-align: center;
  font-family: "Figtree";
  font-size: 68px;
  font-style: normal;
  font-weight: 500;
  line-height: 79.2px;
  letter-spacing: -1.44px;
  background: var(
    --Home-Banner-Title-Gradient,
    radial-gradient(
      82.47% 140.95% at 31.38% -13.87%,
      #fff 36%,
      #ffece0 64%,
      #72b4ff 88.94%
    )
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
}

.blue-text {
  color: #046BE1;
}

.hero-subtitle {
  color: #fff;
  text-align: center;
  font-family: "Figtree";
  font-size: 24px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  align-self: stretch;
  margin: 0;
}

/* Philosophy Section */
.philosophy-section {
  padding: 80px 160px;
  background-color: #fff;
}

.philosophy-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.philosophy-card {
  border-radius: 16px;
  background: var(
    --Advantage-Card-Gradient,
    linear-gradient(180deg, #e4efff 0%, #eef1ff 100%)
  );
  gap: 24px;
  padding: 40px 24px;
  text-align: left;
}

.icon-container {
  margin-bottom: 24px;
}

.icon-box {
  display: flex;
  width: 140px;
  height: 50px;
  border: 1px solid #046BE1;
  border-radius: 8px;
  overflow: hidden;
}

.icon {
  width: 50%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.icon-separator {
  width: 1px;
  height: 100%;
  background-color: #046BE1;
}

.card-title {
  color: var(--Raisin-Black, #1c1c1c);
  font-family: "Figtree";
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  letter-spacing: -0.018px;
  margin: 0;
}

.card-text {
  color: var(--Raisin-Black, #1c1c1c);
  font-family: "Figtree";
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  letter-spacing: -0.018px;
}

/* Product Suite Section */
.product-suite-section {
  padding: 80px 160px;
  background: var(
    --Section-Gradient-Light-BG,
    linear-gradient(78deg, #edf8ff 10.94%, #eff1ff 92.48%)
  );
}

.product-cards {
  max-width: 1200px;
  margin: 0 auto;
}

.product-card {
  background-color: #fff;
  border-radius: 12px;
  margin-bottom: 32px;
  /* padding: 32px; */
  display: flex;
  flex-direction: row;
  gap: 32px;
}

.product-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  flex: 1;
  padding: 40px;
}

.product-gif {
  width: 64px;
  height: 64px;
  aspect-ratio: 1/1;
}

.product-title {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 4px;
  font-family: "Figtree";
  font-size: 32px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.64px;
}

.product-subtitle {
  color: var(--Raisin-Black, #1c1c1c);
  font-family: "Figtree";
  font-size: 20px;
  font-weight: 400;
  line-height: 120%;
  margin: 0;
}

.product-description {
  color: var(--Raisin-Black, #1c1c1c);
  font-family: "Figtree";
  font-weight: 400;
  font-size: 18px;
  font-style: normal;
  line-height: 140%;
  letter-spacing: -0.018px;
}

.development-badge {
  color: var(--Primary---Brandeis-Blue, #046BE1);
  font-family: "Figtree";
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%;
  border-radius: 50px;
  border: 1px solid var(--Primary---Brandeis-Blue, #046BE1);
  padding: 6px 12px;
}

.product-image,
.product-video-container {
  aspect-ratio: 7 / 6;
  width: 50%;
}

.product-video-container {
  padding: 25px;
  aspect-ratio: 12 / 8;
}

.product-video {
  border-radius: 13px;
  box-shadow: 10px 0px 15.8px 0px rgba(0, 0, 0, 0.25);
  height: 100%;
}

/* Data Coverage Section */
.data-coverage-section {
  padding: 80px 160px;
  background-color: #fff;
}

.data-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin: 0;
}

.data-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  border-right: 1px solid #eef1ff;
  gap: 12px;
  padding: 32px;
}
.data-item:nth-child(4n) {
  border-right: none;
}

.data-icon {
  width: 40px;
  height: 40px;
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
}

.data-text {
  color: var(--Raisin-Black, #1c1c1c);
  text-align: center;
  font-family: "Figtree";
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  letter-spacing: -0.018px;
  margin: 0;
}

/* Responsive Styles */
@media (max-width: 1024px) {
  .philosophy-grid {
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }

  .data-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .product-card {
    flex-direction: column;
  }

  /* .product-image {
    min-height: 200px;
  } */
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 50px;
    line-height: 47px;
    text-align: left;
  }
  .hero-subtitle {
    font-size: 22px;
    text-align: left;
  }
  .section-title {
    font-size: 36px;
  }
  .section-subtitle {
    font-size: 18px;
    margin: 1rem 0;
  }
  .philosophy-section,
  .product-suite-section,
  .data-coverage-section,
  .cta-section {
    padding: 45px 40px;
  }
  .philosophy-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .philosophy-card {
    padding: 12px;
    gap: 10px;
  }
  .product-content {
    padding: 25px;
  }
  .product-title {
    font-size: 20px !important;
  }
  .product-subtitle {
    font-size: 18px;
  }
  .product-description {
    font-size: 16px;
    margin: 0.7rem 0;
  }
  .product-image {
    object-fit: cover;
    width: 100%;
    overflow: hidden;
    max-width: 100%;
  }
  .product-card {
    gap: 10px;
    margin-bottom: 15px;
  }
  .product-video {
    width: 100%;
    object-fit: cover;
  }
  .product-video-container {
    width: auto;
    aspect-ratio: auto;
  }
  .card-text {
    font-size: 16px;
    margin: 5px 0;
  }
  .data-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .cta-buttons {
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  .btn {
    width: 100%;
    max-width: 250px;
    justify-content: center;
  }
  .hero-content {
    padding: 9%;
  }
  .data-text {
    font-size: 16px;
  }
}

@media (max-width: 480px) {
  .hero-section {
    height: auto;
  }
  .hero-title {
    font-size: 48px;
    line-height: 56px;
    text-align: left;
  }

  .hero-content {
    padding: 70px 20px 40px 20px;
  }

  .hero-subtitle {
    font-size: 16px;
    text-align: left;
  }

  .section-title {
    font-size: 36px;
    line-height: 45px;
  }

  .section-subtitle {
    font-size: 16px;
    margin: 1rem 0;
  }

  .data-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .philosophy-section {
    padding: 25px;
  }
  .philosophy-card {
    padding: 15px;
    text-align: center;
  }
  .philosophy-grid {
    grid-template-columns: 1fr;
  }
  .card-text {
    font-size: 14px;
    margin: 0.3rem 0;
  }
  .product-card {
    padding: 15px;
    text-align: center;
  }
  .icon-container {
    margin-bottom: 15px;
    display: flex;
    justify-content: center;
  }
  .product-suite-section {
    padding: 25px;
  }
  .product-content {
    padding: 10px;
    align-items: center;
  }
  .product-title {
    font-size: 20px !important;
    text-align: center;
  }
  .product-subtitle {
    font-size: 16px;
    text-align: center;
  }
  .product-description {
    font-size: 14px;
    text-align: center;
  }
  .product-card {
    gap: 5px;
    margin-bottom: 25px;
  }
  .product-image {
    object-fit: cover;
    overflow: hidden;
    max-width: 100%;
    width: 100%;
  }
  .product-video {
    width: 100%;
    object-fit: cover;
  }
  .product-video-container {
    width: auto;
    padding: 5px 15px;
  }
  .development-badge {
    font-size: 14px;
  }
  .data-coverage-section {
    padding: 25px;
  }
  .data-item {
    padding: 10px;
  }
  .data-text {
    font-size: 16px;
  }
}
</style>
